/**
 * Sync Monitor — reads app_config/global.syncHealth (per-sheet) and shows a
 * products-per-category count from the products collection.
 *
 * "Force full re-sync": on the free tier there are no Cloud Functions, so the
 * sheet → Firestore sync is driven entirely from Google Apps Script. This
 * button opens instructions for running the re-sync there rather than trying
 * to trigger it in-app.
 */
import { useMemo, useState } from 'react';
import { useAppConfig, useActiveProducts } from '../hooks/useFirestore';
import type { SyncHealthEntry } from '@mitang/shared';
import { Card, SectionHeader, Button, Badge } from '../components/ui';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { formatDateTime, formatIndianNumber, timeAgo } from '../lib/format';
import { useToast } from '../lib/toast';

export function SyncMonitorPage(): JSX.Element {
  const config = useAppConfig();
  const products = useActiveProducts();
  const toast = useToast();
  const [showResync, setShowResync] = useState(false);

  const sheets = useMemo<SyncHealthEntry[]>(() => {
    const health = config.data?.syncHealth ?? {};
    return Object.values(health).sort((a, b) => b.lastSyncAt - a.lastSyncAt);
  }, [config.data]);

  const perCategory = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of products.data) {
      counts.set(p.category, (counts.get(p.category) ?? 0) + 1);
    }
    return [...counts.entries()].map(([category, count]) => ({ category, count })).sort((a, b) =>
      a.category.localeCompare(b.category),
    );
  }, [products.data]);

  const totalUnparseable = sheets.reduce((s, x) => s + (x.unparseablePriceCount ?? 0), 0);
  const anyError = sheets.some((s) => s.lastStatus === 'error');

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Sync Monitor"
        subtitle="Google Sheets → Firestore sync health, per sheet."
        actions={
          <Button variant="secondary" onClick={() => setShowResync(true)}>
            Force full re-sync…
          </Button>
        }
      />

      {config.data?.updatedAt && (
        <p className="text-xs text-slate-500">
          Config last updated {timeAgo(config.data.updatedAt)} · min app version{' '}
          <span className="font-mono">{config.data.minAppVersion}</span>
        </p>
      )}

      {(anyError || totalUnparseable > 0) && !config.loading && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {anyError && <span>One or more sheets reported an error on last sync.</span>}
          {totalUnparseable > 0 && (
            <span>
              {formatIndianNumber(totalUnparseable)} price cells could not be parsed — fix them in
              the source sheet(s).
            </span>
          )}
        </div>
      )}

      {/* Per-sheet health */}
      {config.loading ? (
        <LoadingState label="Loading sync health…" />
      ) : config.error ? (
        <ErrorState message={config.error} />
      ) : sheets.length === 0 ? (
        <EmptyState
          title="No sync data yet"
          hint="Once Apps Script posts a sheet, its health will appear here."
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Sheet</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Last sync</th>
                  <th className="px-4 py-3 text-right font-medium">Rows</th>
                  <th className="px-4 py-3 text-right font-medium">Active</th>
                  <th className="px-4 py-3 text-right font-medium">Unparseable ₹</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sheets.map((s) => (
                  <tr key={s.sheetName} className="align-top">
                    <td className="px-4 py-3 font-medium text-slate-800">{s.sheetName}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={s.lastStatus} />
                      {s.lastError && (
                        <p className="mt-1 max-w-xs text-[11px] text-red-600">{s.lastError}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600" title={formatDateTime(s.lastSyncAt)}>
                      {timeAgo(s.lastSyncAt)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                      {formatIndianNumber(s.rowCount)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                      {formatIndianNumber(s.activeCount)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {s.unparseablePriceCount > 0 ? (
                        <span className="font-semibold text-amber-600">
                          {formatIndianNumber(s.unparseablePriceCount)}
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Products per category */}
      <Card>
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 className="text-sm font-semibold text-slate-900">Products per category</h2>
          <p className="text-xs text-slate-500">Active products currently in Firestore</p>
        </div>
        <div className="p-5">
          {products.loading ? (
            <LoadingState label="Counting products…" />
          ) : products.error ? (
            <ErrorState message={products.error} />
          ) : perCategory.length === 0 ? (
            <EmptyState title="No active products" />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {perCategory.map((c) => (
                <div key={c.category} className="rounded-lg border border-slate-200 p-3">
                  <p className="truncate text-xs font-medium uppercase tracking-wide text-slate-500">
                    {c.category}
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-slate-900">
                    {formatIndianNumber(c.count)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Force re-sync instructions modal (documents the limitation) */}
      {showResync && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setShowResync(false)} aria-hidden />
          <div className="relative w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-slate-900">Force full re-sync</h3>
            <p className="mt-2 text-sm text-slate-600">
              The sheet → Firestore sync is driven from <strong>Google Apps Script</strong>, not
              from this admin panel. A full re-sync must be started there.
            </p>
            <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-slate-600">
              <li>Open the linked Google Sheet.</li>
              <li>
                Extensions → Apps Script, then run the <code className="rounded bg-slate-100 px-1">syncAll</code>{' '}
                function (or use the custom “Sync” menu).
              </li>
              <li>Watch this page — sheet timestamps update as each tab finishes.</li>
            </ol>
            <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
              Free tier: there are no Cloud Functions, so re-sync always runs from Apps Script.
              This panel cannot trigger it directly.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowResync(false)}>
                Close
              </Button>
              <Button
                onClick={() => {
                  navigator.clipboard?.writeText('syncAll').catch(() => undefined);
                  toast.info('Copied "syncAll" — run it in Apps Script.');
                  setShowResync(false);
                }}
              >
                Copy function name
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: SyncHealthEntry['lastStatus'] }): JSX.Element {
  if (status === 'ok') return <Badge tone="success">ok</Badge>;
  if (status === 'partial') return <Badge tone="warn">partial</Badge>;
  return <Badge tone="danger">error</Badge>;
}
